(() => {
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let paused = preference.matches;
  let frame = 0;
  let time = 0;
  let last = 0;
  const colors = {};
  const canvases = [];
  const toggles = new Set();
  const updateButtons = () => {
    document.documentElement.classList.toggle('motion-paused', paused);
    for (const button of toggles) {
      button.hidden = false;
      button.setAttribute('aria-pressed', String(paused));
      button.querySelector('[data-motion-label]').textContent = paused ? button.dataset.resume : button.dataset.pause;
      button.querySelector('.motion-toggle__icon').textContent = paused ? '▷' : 'Ⅱ';
    }
  };
  const draw = (state, t) => {
    const { context: ctx, width: w, height: h, style, texture } = state;
    if (!ctx || !w || !h) return;
    ctx.clearRect(0, 0, w, h);
    const hero = state.canvas.dataset.signal === 'hero';
    const cx = w * .5;
    const cy = h * .5;
    if (hero) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-.32);
      const gradient = ctx.createLinearGradient(-w * .5, 0, w * .5, 0);
      gradient.addColorStop(0, colors.lilac);
      gradient.addColorStop(.55, colors.rose);
      gradient.addColorStop(1, colors.peach);
      for (let line = 0; line < 45; line++) {
        ctx.beginPath();
        for (let point = 0; point <= 220; point++) {
          const x = (point / 220 - .5) * w * 1.25;
          const envelope = Math.exp(-Math.pow(x / (w * .34), 2));
          const y = (line - 22) * 5.5 + Math.sin(x / w * 9 + t * .48 + line * .085) * h * .22 * envelope + Math.sin(x / w * 19 - t * .2 + line * .06) * 18 * envelope;
          if (point) ctx.lineTo(x, y); else ctx.moveTo(x, y);
        }
        ctx.strokeStyle = gradient;
        ctx.globalAlpha = .11 + Math.sin(line / 45 * Math.PI) * .42;
        ctx.lineWidth = .8;
        ctx.stroke();
      }
      ctx.restore();
    } else {
      const size = Math.min(w, h) * (.32 + texture * .06);
      const lobes = 3 + style % 5;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(t * .07);
      const gradient = ctx.createLinearGradient(-size, -size, size, size);
      gradient.addColorStop(0, colors.lilac);
      gradient.addColorStop(.55, colors.rose);
      gradient.addColorStop(1, colors.peach);
      for (let line = 0; line < 30; line++) {
        ctx.beginPath();
        for (let point = 0; point <= 240; point++) {
          const angle = point / 240 * Math.PI * 2;
          const modulation = Math.sin(angle * lobes + t * .5 + line * .07) * texture * .24 + Math.cos(angle * (lobes + 2) - t * .24) * .075;
          const radius = size * (.42 + line / 38) * (1 + modulation);
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius * .89;
          if (point) ctx.lineTo(x, y); else ctx.moveTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = gradient;
        ctx.globalAlpha = .16 + line / 50;
        ctx.lineWidth = .8;
        ctx.stroke();
      }
      ctx.restore();
      ctx.fillStyle = colors.text;
      ctx.fillRect(cx - 2, cy - 2, 4, 4);
    }
  };
  const redraw = () => canvases.forEach(state => draw(state, time));
  const canAnimate = () => !paused && !document.hidden && canvases.some(state => state.visible);
  const tick = now => {
    frame = 0;
    if (!canAnimate()) { last = 0; return; }
    if (!last || now - last >= 32) {
      if (last) time += Math.min((now - last) / 1000, .1);
      last = now;
      canvases.filter(state => state.visible).forEach(state => draw(state, time));
    }
    frame = requestAnimationFrame(tick);
  };
  const schedule = () => {
    if (!canAnimate()) { if (frame) cancelAnimationFrame(frame); frame = 0; last = 0; }
    else if (!frame) frame = requestAnimationFrame(tick);
  };
  const resize = new ResizeObserver(entries => {
    for (const entry of entries) {
      const state = canvases.find(item => item.canvas === entry.target);
      if (!state?.context) continue;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      state.width = entry.contentRect.width;
      state.height = entry.contentRect.height;
      state.canvas.width = Math.round(state.width * ratio);
      state.canvas.height = Math.round(state.height * ratio);
      state.context.setTransform(ratio, 0, 0, ratio, 0, 0);
      draw(state, time);
    }
  });
  const visibility = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = canvases.find(item => item.canvas === entry.target);
      if (state) state.visible = entry.isIntersecting;
    }
    schedule();
  });
  preference.addEventListener('change', event => { paused = event.matches; updateButtons(); schedule(); });
  document.addEventListener('visibilitychange', schedule);
  const initialize = root => {
  const themeColors = getComputedStyle(document.documentElement);
  for (const name of ['lilac', 'rose', 'peach', 'text']) colors[name] = themeColors.getPropertyValue(`--${name}`).trim();
  root.querySelectorAll('[data-signal]').forEach(canvas => {
    if (canvases.some(state => state.canvas === canvas)) return;
    const state = { canvas, context: canvas.getContext('2d'), visible: false, width: 0, height: 0, style: 1, texture: .55 };
    canvases.push(state); resize.observe(canvas); visibility.observe(canvas);
  });
  root.querySelectorAll('[data-motion-toggle]').forEach(button => {
    if (toggles.has(button)) return;
    toggles.add(button);
    button.addEventListener('click', () => { paused = !paused; updateButtons(); schedule(); });
  });
  for (const lab of root.querySelectorAll('[data-style-lab]')) {
    if (lab.dataset.effectsReady) continue;
    lab.dataset.effectsReady = 'true';
    const state = canvases.find(item => item.canvas === lab.querySelector('canvas'));
    for (const button of lab.querySelectorAll('[data-style]')) {
      button.addEventListener('click', () => {
        lab.querySelectorAll('[data-style]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        lab.querySelector('[data-style-name]').textContent = button.querySelector('strong').textContent;
        lab.querySelector('[data-style-description]').textContent = button.dataset.description;
        lab.querySelector('[data-style-number]').textContent = `${button.dataset.style.padStart(2, '0')} / 24`;
        if (state) { state.style = Number(button.dataset.style); draw(state, time); }
      });
    }
    lab.querySelector('[data-texture]')?.addEventListener('input', event => {
      if (state) { state.texture = Number(event.target.value) / 100; draw(state, time); }
    });
  }
  if (window.matchMedia('(pointer: fine)').matches) {
    for (const shot of root.querySelectorAll('[data-parallax]')) {
      if (shot.dataset.effectsReady) continue;
      shot.dataset.effectsReady = 'true';
      const stage = shot.parentElement;
      stage.addEventListener('pointermove', event => {
        if (paused) return;
        const rect = stage.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        shot.style.transform = `perspective(1400px) rotateY(${-5 + x * 7}deg) rotateX(${2 - y * 5}deg) translateY(${-y * 5}px)`;
      });
      stage.addEventListener('pointerleave', () => { shot.style.transform = ''; });
    }
  }
  root.querySelectorAll('[data-reveal]').forEach(element => reveals.observe(element));
  updateButtons();
  redraw();
  schedule();
  };
  const reveals = new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); reveals.unobserve(entry.target); }
  }, { threshold: .12 });
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (let i = canvases.length - 1; i >= 0; i--) {
      if (!event.target.contains(canvases[i].canvas)) continue;
      resize.unobserve(canvases[i].canvas); visibility.unobserve(canvases[i].canvas); canvases.splice(i, 1);
    }
    for (const button of toggles) if (event.target.contains(button)) toggles.delete(button);
    event.target.querySelectorAll('[data-reveal]').forEach(element => reveals.unobserve(element));
    schedule();
  });
  document.addEventListener('shopify:block:select', event => {
    const row = event.target.closest('details');
    if (row) row.open = true;
    const reveal = event.target.closest('[data-reveal]');
    if (reveal) reveal.classList.add('is-revealed');
  });
})();
